import { isOutcomeEphemeral } from 'flow/service/outcomes/outcomes';
import { ActionType } from '../constants';
import type {
  Action,
  SchemaInstance,
  WorkflowProps,
} from '../types';
import {
  getActionPublishedAttributeToken,
  getTagActionsByTagName,
} from 'flow/service/actions';
import { isTagEphemeral } from 'flow/service/tags/tags';
import { getActionsByPublishedAttributeToken, isPublishedAttributeEphemeral } from 'flow/service/publishedAttributes';
import { getListActionsByListName, isListAction } from 'flow/service/actions/services/lists';
import { isListEphemeral } from 'flow/service/lists';
import { removeEphemeralOutcomeFromTags } from 'flow/store/redux/helpers';

export function modifyRelevantPropsFromActionRemoval({
  actionToRemove,
  workflowProps,
  workflowActions,
}: {
  actionToRemove: Action;
  workflowProps: WorkflowProps;
  workflowActions: Partial<SchemaInstance>['actions'];
}) {
  switch (actionToRemove.type) {
    case ActionType.TAG: {
      const { tag: { name } } = actionToRemove;
      // check remaining actions to see if tag is used elsewhere
      const isTagInWorkflow = getTagActionsByTagName(
        workflowActions,
        name,
      ).filter(action => action.publicId !== actionToRemove.publicId).length > 0;
      // if this action is only instance of tag in wf, remove from props
      if (!isTagInWorkflow) {
        // remove tag from props
        workflowProps.tags = workflowProps.tags.filter(
          tag => !(isTagEphemeral(tag) && tag.tag_name === name),
        );
      }
      break;
    }
    case ActionType.OUTCOME: {
      const { outcome: { name } } = actionToRemove;
      /**
       * if used in any tags, this outcome must be removed
       * this means:
       * - all tags actions using this outcome need to be updated
       * - all connected tag props need to be updated
       */

      const { updatedActions, updatedTags } = removeEphemeralOutcomeFromTags({
        actions: workflowActions,
        tags: workflowProps.tags,
        outcomeName: name,
      });

      Object.assign(workflowActions, updatedActions);
      workflowProps.tags = updatedTags;
      workflowProps.outcomes = workflowProps.outcomes.filter(
        outcome => !(isOutcomeEphemeral(outcome) && outcome.outcome === name));

      break;
    }
    case ActionType.INPUT:
    case ActionType.OUTPUT: {
      const paToken = getActionPublishedAttributeToken(actionToRemove);
      // check if PA is used elsewhere (can be used by both input + output in same WF)
      const connectedActions = getActionsByPublishedAttributeToken(workflowActions, paToken)
        .filter(action => action.publicId !== actionToRemove.publicId);
      // only remove if actionToRemove is the only connected action
      if (connectedActions.length === 0) {
        workflowProps.publishedAttributes = workflowProps.publishedAttributes.filter(
          pa => !(isPublishedAttributeEphemeral(pa) && pa.attributeToken === paToken),
        );
      }

      break;
    }
    case ActionType.SERVICE: {
      if (isListAction(actionToRemove)) {
        const { service: { name } } = actionToRemove;
        const isListUsedInWorkflow = getListActionsByListName(workflowActions, name)
          .filter(action => action.publicId !== actionToRemove.publicId).length > 0;
        if (!isListUsedInWorkflow) {
          workflowProps.lists = workflowProps.lists.filter(
            list => !(isListEphemeral(list) && list.list_name === name),
          );
        }
      }
      break;
    }
  }
}
